import { memo, type ComponentProps } from "react";
import { cva } from "class-variance-authority";

import { cn } from "../../lib/utils";

const inputVariants = cva(
  [
    "flex h-8 w-full min-w-0 rounded-[10px] border px-3 py-2 text-base md:text-sm",
    "border-input bg-transparent dark:bg-input/30 placeholder:text-muted-foreground",
    "selection:bg-primary selection:text-primary-foreground shadow-xs transition-[color,box-shadow] outline-none",
    "disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50",
    // Tailwind 3 has no built-in `aria-invalid:` variant (it compiles to nothing), hence the arbitrary form.
    "aria-[invalid=true]:border-destructive",
    "file:mr-3 file:h-full file:cursor-pointer file:border-0 file:border-r file:border-solid file:border-input file:bg-muted file:px-3 file:text-sm file:font-medium file:text-foreground hover:file:bg-accent",
  ],
  {
    variants: {
      variant: {
        default: "",
        inline:
          "h-auto rounded-none border-transparent bg-transparent p-0 shadow-none dark:bg-transparent",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

export interface InputProps extends ComponentProps<"input"> {
  /**
   * `inline` drops the field chrome (border, background, shadow, padding) so the input
   * reads as editable text in place — e.g. a renameable title. Typography is inherited from
   * `className`; an `aria-invalid` border still shows.
   * @default "default"
   */
  variant?: "default" | "inline";
}

/**
 * A single-line text field. Memoized so a controlled Input skips re-rendering when its parent form
 * re-renders on an unrelated field's keystroke — keep its props referentially stable to benefit.
 *
 * @example
 * ```tsx
 * <Label htmlFor="email">Email</Label>
 * <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
 * ```
 */
export const Input = memo(({ className, type, variant, ...props }: InputProps) => (
  <input
    type={type}
    data-slot="input"
    className={cn(inputVariants({ variant }), type === "file" && "cursor-pointer overflow-hidden p-0 text-muted-foreground", className)}
    {...props}
  />
));
Input.displayName = "Input";
